import type { UnhandledRequestCallback } from "msw";

import { bffOrigin } from "@/lib/bffBase";

/**
 * Same-origin paths MSW lets through untouched in mock mode because Next
 * itself serves them. Nothing same-origin reaches the BFF any more (the
 * browser calls the BFF origin directly, lib/bffTransport.ts), so these are
 * the routes that exist rather than a guard:
 *  - `/_next/*`, `/__nextjs*`: Next internals;
 *  - `/api/whitelabels/<…>.<ext>`: the local whitelabel route
 *    (`app/api/whitelabels/[...path]/route.ts`). Dotted paths only: every URL
 *    the app builds for it ends in a file extension (`utils/whitelabel.ts`), so
 *    a dotless one is refused like any other unserved `/api/*` path;
 *  - `/monitoring`: the Sentry tunnel route (outside proxy.ts's matcher).
 * Every other same-origin `/api/*` path has no route here; it is refused like
 * a BFF call, since only stale code would still send one.
 */
const SAME_ORIGIN_PASSTHROUGH = [
  /^\/_next\//,
  /^\/__nextjs/,
  /^\/api\/whitelabels\/.*\./,
  /^\/monitoring/,
];

/**
 * Cross-origin hosts that are the real backend. A mock-mode page must never
 * reach them; everything else cross-origin (whitelabel S3, fonts, SumSub) is
 * a third party and passes through. The BFF origin is answered by the
 * handlers (the last one is a 501 catch-all, mocks/bffBase.ts), so one that
 * still arrives here was sent past them.
 */
const isBackendHost = (host: string): boolean => {
  if (host === new URL(bffOrigin()).host) return true;
  if (host.startsWith("api-customer.")) return true;
  // next.config's default Keycloak host (`auth.<env>.<platform>.endor-group.net`),
  // whatever NEXT_PUBLIC_AUTH_URL says.
  if (host.startsWith("auth.") && host.endsWith(".endor-group.net"))
    return true;
  const authUrl = process.env.NEXT_PUBLIC_AUTH_URL;
  if (!authUrl) return false;
  try {
    return new URL(authUrl).host === host;
  } catch {
    // A malformed auth URL names no host, so it cannot be this one.
    return false;
  }
};

const currentOrigin = () =>
  typeof window === "undefined" ? undefined : window.location.origin;

/**
 * Whether an unhandled request in mock mode may go to the network. Same
 * origin: anything outside `/api/`, plus the Next-served routes above.
 * Cross-origin: anything but the real backend (the BFF origin, Keycloak).
 */
export function isPassthroughRequest(
  url: URL,
  origin: string | undefined = currentOrigin()
): boolean {
  if (url.origin !== origin) return !isBackendHost(url.host);
  if (!url.pathname.startsWith("/api/")) return true;
  return SAME_ORIGIN_PASSTHROUGH.some((re) => re.test(url.pathname));
}

/**
 * MSW `onUnhandledRequest` for mock mode: passthroughs go to the network;
 * every other unmatched request — a call to the BFF origin or Keycloak, or a
 * same-origin `/api/*` path with no route — fails here instead of reaching a
 * real backend.
 */
export const onUnhandledRequest: UnhandledRequestCallback = (
  request,
  print
) => {
  const url = new URL(request.url);
  if (isPassthroughRequest(url)) return;
  console.warn(
    url.origin === currentOrigin()
      ? `[mock-mode] Unmocked ${request.method} ${url.pathname} — no route serves it`
      : `[mock-mode] Blocked ${request.method} ${url.origin}${url.pathname} — a mock-mode page must never call the real backend`
  );
  // MSW's "error" strategy: throws inside MSW's own request pipeline. The
  // browser client catches that and resolves the fetch with a plain HTTP 500
  // "Request Handler Error" — it does NOT reject as a network error. RTK
  // Query's shouldRetryRequest treats a bare 500 as non-retryable, so this
  // must never silently fall through to a real backend.
  print.error();
};
